import React, { useState, useEffect, useRef, useContext } from 'react'

import axios from 'axios'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxPhoneNumber from 'components/reusable/AdxPhoneNumber/AdxPhoneNumber'

import { Formik, Field, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'

import { companyTypes, countries } from 'services/api/dropdown'
import { postBasicCompanyDetails } from 'services/api/company-details'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import { presignURL, storeOrganizationLogo } from 'services/api/presign-url'

import { authContext } from 'contexts/AuthContext'
import AdxDropzoneCompanyImage from 'components/reusable/AdxDropzoneCompanyImage'
import Bugsnag from '@bugsnag/js'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'

const EditBasicDetailsModal = ({
  showModalEditBasicDetails,
  handleCloseModalEditBasicDetails,
  companyDetails,
}) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const { auth } = useContext(authContext)

  // State for AdxDropzone

  const [arrayOfStates, setArrayOfStates] = useState([])

  const { data: companyTypesQuery } = useQuery('companyTypesQuery', companyTypes)
  const { data: countriesQuery } = useQuery('countriesQuery', countries)

  const [photo, setPhoto] = useState(null)

  // state and function for AdxDropdown components
  const [selectValueCompanyType, setSelectValueCompanyType] = useState('')
  const [selectValueState, setSelectValueState] = useState('')
  const [selectValueCountry, setSelectValueCountry] = useState('')

  const handleSetSelectValueCompanyType = (event) => {
    setSelectValueCompanyType(event.target.value)
  }

  const handleSetSelectState = (event) => {
    setSelectValueState(event.target.value)
  }

  const handleSetSelectCountry = (event) => {
    setSelectValueCountry(event.target.value)
  }

  useEffect(() => {
    setSelectValueCompanyType(companyDetails?.data?.company_type)
    setSelectValueCountry(companyDetails?.data?.country)
    setSelectValueState(companyDetails?.data?.state)
    let states = countriesQuery?.data.find(
      (item) => item.name === companyDetails?.data?.country
    )?.states
    setArrayOfStates(states)
  }, [companyDetails, countriesQuery])

  const initialValues = {
    organization_name: companyDetails?.data?.organization_name,
    company_type: companyDetails?.data?.company_type,
    ssm_number: companyDetails?.data?.ssm_number,
    company_phone_no: companyDetails?.data?.company_phone_no,
    company_phone_country_id: companyDetails?.data?.company_phone_country_id,
    address: companyDetails?.data?.address,
    postcode: companyDetails?.data?.postcode,
    state: companyDetails?.data?.state,
    country: companyDetails?.data?.country,
  }

  const validationSchema = Yup.object().shape({
    organization_name: Yup.string().required('Company name is required'),
    company_type: Yup.number().required('Company type is required'),
    ssm_number: Yup.string()
      .required('Registration number is required')
      .matches(/^[a-zA-Z0-9-]+$/, 'Only alphabets are allowed for this field '),
    company_phone_country_id: Yup.string().required('Country code is required'),
    company_phone_no: Yup.string()
      .required('Company phone required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field '),
    address: Yup.string().required('Address is required'),
    postcode: Yup.string()
      .required('Postcode is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field '),
    state: Yup.string().required('State is required').nullable(),
    country: Yup.string().required('Country is required').nullable(),
  })

  const [errorModal, setErrorModal] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const mutation = useMutation(postBasicCompanyDetails, {
    onSuccess: async () => {
      if (auth.data.tenant_id === undefined) {
        throw new Error('Internal error. Tenant id is undefined.')
      }
      await uploadPhoto()
      await queryClient.invalidateQueries('companyDetails')
      handleCloseModal()
    },
    onError: async (e) => {
      if (e.status === 422) {
        handleValidationErrors(e, formikRef)
      } else {
        setErrorMsg(e.message)
        setErrorModal(true)
        Bugsnag.notify(e)
      }
    },
  })

  const handleOnSubmit = async (values) => {
    await mutation.mutate(values)
  }

  const uploadPhoto = async () => {
    try {
      const results = await presignURL({
        bucket: process.env.REACT_APP_AWS_BUCKET,
        key: `tenant_${auth.data.tenant_id}/organization_logo/` + photo.name,
      })
      const awsRequest = axios.create()
      delete awsRequest.defaults.headers.common['Authorization']
      await awsRequest.put(results.data.url, photo, {
        headers: {
          'Content-Type': photo.type,
        },
      })
      await storeOrganizationLogo({ organization_logo: photo.name })
    } catch (e) {
      Bugsnag.notify(e)
    }
  }

  const handleCloseModal = () => {
    setPhoto(null)
    handleCloseModalEditBasicDetails()
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, action) => handleOnSubmit(values, action)}
        innerRef={formikRef}
      >
        {({ values, errors, touched, handleChange, handleSubmit, handleBlur, setFieldValue }) => (
          <AdxFormModal
            title="Basic company details"
            subtitle=""
            open={showModalEditBasicDetails}
            onClose={handleCloseModal}
            handleClose={handleCloseModal}
            enableFooter
            actionText="Save"
            handleAction={handleSubmit}
          >
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box display="flex" justifyContent="center">
                  <AdxDropzoneCompanyImage
                    photoUpload={photo}
                    setPhotoUpload={setPhoto}
                    file={companyDetails?.data?.organization_logo}
                    radiusLength={90}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Field
                      as={AdxInput}
                      name="organization_name"
                      label="Company name"
                      size="standard"
                      placeholder="Company name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="organization_name" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.organization_name && touched.organization_name}
                    />
                  </Box>
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={7}>
                        <Field
                          as={AdxDropdown}
                          name="company_type"
                          label="Company type"
                          value={selectValueCompanyType}
                          onChange={(e) => {
                            handleSetSelectValueCompanyType(e)
                            setFieldValue('company_type', e.target.value)
                          }}
                          onBlur={handleBlur}
                          isErrorShown={errors.company_type && touched.company_type}
                          errorMessage={
                            <ErrorMessage name="company_type" component={AdxErrorMessage} />
                          }
                        >
                          {companyTypesQuery?.data?.company_types.map((item) => (
                            <AdxMenuItem
                              key={item.id}
                              value={item.id}
                              selectedvalue={selectValueCompanyType}
                            >
                              {item.description}
                            </AdxMenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={12} lg={5}>
                        <Field
                          as={AdxInput}
                          name="ssm_number"
                          label="Registration Number"
                          size="standard"
                          placeholder="Registration Number"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errorMessage={
                            <ErrorMessage name="ssm_number" component={AdxErrorMessage} />
                          }
                          isErrorShown={errors.ssm_number && touched.ssm_number}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    <AdxPhoneNumber
                      label="Primary Phone Number"
                      placeholder="E.g: 123456789"
                      sxInput={{ width: '100%' }}
                      dropdownDisabled
                      dropdownValue={values.company_phone_country_id}
                      dropdownName="company_phone_country_id"
                      onDropdownChange={(value) => setFieldValue(`company_phone_country_id`, value)}
                      dropdownErrorMessage={
                        touched.company_phone_country_id && errors.company_phone_country_id
                      }
                      inputValue={values.company_phone_no}
                      inputName="company_phone_no"
                      onInputChange={handleChange}
                      inputErrorMessage={touched.company_phone_no && errors.company_phone_no}
                      dropdownIsErrorShown={
                        touched.company_phone_country_id && errors.company_phone_country_id
                      }
                      inputIsErrorShown={touched.company_phone_no && errors.company_phone_no}
                    />
                  </Box>
                  <Field
                    as={AdxInput}
                    name="address"
                    label="Primary address"
                    size="standard"
                    placeholder="Primary address"
                    multiline
                    minRows={3}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={<ErrorMessage name="address" component={AdxErrorMessage} />}
                    isErrorShown={errors.address && touched.address}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={4}>
                      <Field
                        as={AdxInput}
                        name="postcode"
                        label="Postcode"
                        size="standard"
                        placeholder="Postcode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorMessage={<ErrorMessage name="postcode" component={AdxErrorMessage} />}
                        isErrorShown={errors.postcode && touched.postcode}
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Field
                        as={AdxDropdown}
                        name="state"
                        label="State"
                        value={selectValueState}
                        onChange={(e) => {
                          setFieldValue('state', e.target.value)
                          handleSetSelectState(e)
                        }}
                        isErrorShown={errors.state && touched.state}
                        errorMessage={<ErrorMessage name="state" component={AdxErrorMessage} />}
                      >
                        {arrayOfStates?.map((item) => (
                          <AdxMenuItem value={item.name} selectedvalue={selectValueState}>
                            {item.name}
                          </AdxMenuItem>
                        ))}
                      </Field>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Field
                        as={AdxDropdown}
                        name="country"
                        label="Country"
                        disabled
                        value={selectValueCountry}
                        onChange={(e) => {
                          handleSetSelectCountry(e)
                          setFieldValue('country', e.target.value)
                          setFieldValue('state', null)
                          setArrayOfStates(
                            countriesQuery?.data.find((item) => item.name === e.target.value).states
                          )
                        }}
                        isErrorShown={errors.country && touched.country}
                        errorMessage={<ErrorMessage name="country" component={AdxErrorMessage} />}
                      >
                        {countriesQuery?.data.map((item) => (
                          <AdxMenuItem value={item.name} selectedvalue={selectValueCountry}>
                            {item.name}
                          </AdxMenuItem>
                        ))}
                      </Field>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Form>
          </AdxFormModal>
        )}
      </Formik>

      <AdxInfoModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        muiIcon="error"
        iconColor="error.main"
        title="Error"
        subtitle={errorMsg}
        footerButtons
        firstBtnLabel={'Ok'}
        firstBtnHandle={() => setErrorModal(false)}
      ></AdxInfoModal>
    </>
  )
}

export default EditBasicDetailsModal
